import type { GridColumnMeta } from '@/common/components/molecules/modernGrid/modernGrid.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type BaseGridCompatibleRowData = any;

export type BaseGridCompatibleRow<T> = [number, T, BaseGridCompatibleRowData] & {
  [key: string]: unknown;
  [key: number]: number | T | BaseGridCompatibleRowData;
};

export interface BaseGridCompatibleColumn {
  index: number;
  field: string;
  caption?: string;
  meta?: GridColumnMeta;
  [key: string]: unknown;
}

export interface BaseGridCompatibleSlotItem<T> {
  row: BaseGridCompatibleRow<T>;
  column: BaseGridCompatibleColumn;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  fieldName: string;
}

interface Row2ProxyParams {
  originalRow: Record<string | number, unknown>;
  indexToField: Record<number, string>;
  originalRows?: unknown[];
  rowIndex: number;
  isArrayFormat?: boolean;
  isTreeMode?: boolean;
}

interface DataProxyParams {
  originalRow: Record<string, unknown>;
  treeStateFields: Set<string>;
  getOriginalNode: () => Record<string, unknown> | null;
}

// --- Helper Functions ---

// 정적 프로퍼티 접근(`n.id`)으로 V8 monomorphic IC 유지. 동적 접근(`n[field]`)으로 통합하면
// hot path가 megamorphic으로 떨어져 트리 그리드 스크롤 lag을 유발할 수 있음
const findNodeById = (nodes: unknown[], targetId: unknown): Record<string, unknown> | null => {
  for (const node of nodes) {
    const n = node as Record<string, unknown>;
    if (n.id === targetId) return n;
    if (n.children && Array.isArray(n.children)) {
      const found = findNodeById(n.children as unknown[], targetId);
      if (found) return found;
    }
  }
  return null;
};

const createRow2Proxy = ({
  originalRow,
  indexToField,
  originalRows,
  rowIndex,
  isArrayFormat,
  isTreeMode,
}: Row2ProxyParams): Record<string | number, unknown> => {
  return new Proxy(originalRow, {
    get(target, prop) {
      const numProp = typeof prop === 'string' ? Number(prop) : NaN;

      if (!Number.isNaN(numProp) && numProp in indexToField) {
        if (!isTreeMode) {
          if (isArrayFormat && originalRows?.[rowIndex]) {
            return (originalRows[rowIndex] as unknown[])[numProp];
          }
          if (!isArrayFormat && originalRows?.[rowIndex]) {
            return (originalRows[rowIndex] as Record<string, unknown>)[indexToField[numProp]];
          }
        }
        return target[indexToField[numProp]];
      }

      if (typeof prop === 'string') {
        if (!isTreeMode && !isArrayFormat && originalRows?.[rowIndex]) {
          return (originalRows[rowIndex] as Record<string, unknown>)[prop];
        }
        return target[prop];
      }
      return undefined;
    },
    set(target, prop, value) {
      const numProp = typeof prop === 'string' ? Number(prop) : NaN;

      if (!Number.isNaN(numProp) && numProp in indexToField) {
        const fieldName = indexToField[numProp];
        if (!isTreeMode) {
          if (isArrayFormat && originalRows?.[rowIndex]) {
            (originalRows[rowIndex] as unknown[])[numProp] = value;
          }
          if (!isArrayFormat && originalRows?.[rowIndex]) {
            (originalRows[rowIndex] as Record<string, unknown>)[fieldName] = value;
          }
        }
        target[fieldName] = value;
        return true;
      }

      if (typeof prop === 'string') {
        if (!isTreeMode && !isArrayFormat && originalRows?.[rowIndex]) {
          (originalRows[rowIndex] as Record<string, unknown>)[prop] = value;
        }
        target[prop] = value;
        return true;
      }
      return true;
    },
    has(target, prop) {
      const numProp = typeof prop === 'string' ? Number(prop) : NaN;
      if (!Number.isNaN(numProp) && numProp in indexToField) return true;
      return prop in target;
    },
    ownKeys(target) {
      const numKeys = Object.keys(indexToField);
      const fieldKeys = Object.keys(target);
      return [...new Set([...numKeys, ...fieldKeys])];
    },
    getOwnPropertyDescriptor(target, prop) {
      const numProp = typeof prop === 'string' ? Number(prop) : NaN;
      if (!Number.isNaN(numProp) && numProp in indexToField) {
        return { enumerable: true, configurable: true, writable: true };
      }
      if (typeof prop === 'string' && prop in target) {
        return { enumerable: true, configurable: true, writable: true };
      }
      return undefined;
    },
  });
};

const createDataProxy = ({
  originalRow,
  treeStateFields,
  getOriginalNode,
}: DataProxyParams): Record<string, unknown> => {
  return new Proxy(originalRow, {
    get(target, prop) {
      if (typeof prop !== 'string') return undefined;

      // 트리 상태 필드는 target에서 읽기 (원본에 없음)
      if (treeStateFields.has(prop)) return target[prop];

      const originalNode = getOriginalNode();
      if (originalNode && prop in originalNode) return originalNode[prop];
      return target[prop];
    },
    set(target, prop, value) {
      if (typeof prop !== 'string') return true;

      const originalNode = getOriginalNode();
      if (originalNode) originalNode[prop] = value;
      target[prop] = value;
      return true;
    },
    has(target, prop) {
      return prop in target;
    },
    ownKeys(target) {
      return Object.keys(target);
    },
    getOwnPropertyDescriptor(target, prop) {
      if (typeof prop === 'string' && prop in target) {
        return { enumerable: true, configurable: true, writable: true };
      }
      return undefined;
    },
  });
};

// --- Main Function ---

export interface CreateBaseGridSlotItemParams<T extends Record<string, unknown>> {
  modernItem: {
    row: T & { rowId?: string; isDetail?: boolean };
    rowIndex: number;
    column: Record<string, unknown>;
    cell?: unknown;
    depth?: number;
  };
  columnFieldName: string;
  columns?: Array<{ field: string }>;
  fieldToIndexMap?: Record<string, number>;
  originalRows?: unknown[];
  isArrayFormat?: boolean;
  isTreeMode?: boolean;
  isDisabled?: boolean;
  rowIdIndex?: Map<unknown, Record<string, unknown>>;
  idIndex?: Map<unknown, Record<string, unknown>>;
}

export const createBaseGridSlotItem = <T extends Record<string, unknown>>({
  modernItem,
  columnFieldName,
  columns,
  fieldToIndexMap,
  originalRows,
  isArrayFormat,
  isTreeMode,
  isDisabled,
  rowIdIndex,
  idIndex,
}: CreateBaseGridSlotItemParams<T>): BaseGridCompatibleSlotItem<T> => {
  const originalRow = modernItem.row;
  const rowData = originalRow as Record<string, unknown>;
  const rowIndex = modernItem.rowIndex;

  const indexToField: Record<number, string> = {};
  columns?.forEach((col, idx) => {
    indexToField[idx] = col.field;
  });

  const row2 = createRow2Proxy({
    originalRow: originalRow as Record<string | number, unknown>,
    indexToField,
    originalRows,
    rowIndex,
    isArrayFormat,
    isTreeMode,
  });

  const baseArray: unknown[] = [modernItem.rowIndex, originalRow, row2];
  baseArray[6] = isDisabled ?? false;

  const rowArray = Object.assign(baseArray, originalRow) as unknown as BaseGridCompatibleRow<T>;

  const columnIndex = fieldToIndexMap?.[columnFieldName] ?? 0;

  // 트리 상태 필드 초기화
  const dataForSlot = originalRow as T & {
    level?: number;
    index?: number;
    show?: boolean;
    isFilter?: boolean;
  };

  if (dataForSlot.level === undefined) {
    dataForSlot.level = (rowData.level as number) ?? modernItem.depth ?? 0;
  }
  if (dataForSlot.index === undefined) dataForSlot.index = modernItem.rowIndex;
  if (dataForSlot.show === undefined) dataForSlot.show = (rowData.show as boolean) ?? true;
  if (dataForSlot.isFilter === undefined) dataForSlot.isFilter = true;

  const treeStateFields = new Set(['level', 'index', 'show', 'isFilter', 'depth']);

  // 트리 모드: rowId 우선 검색(id 교차검증 포함) → id 폴백. 플랫 구조: rowIndex 우선 → id 폴백.
  // 인덱스 맵이 주입된 경우 O(1) lookup, 없으면 정적 함수로 재귀 fallback.
  const getOriginalNode = (): Record<string, unknown> | null => {
    if (!originalRows) return null;

    if (isTreeMode) {
      const row = originalRow as Record<string, unknown>;
      const targetRowId = row.rowId;
      const byRowId = targetRowId != null ? (rowIdIndex?.get(targetRowId) ?? null) : null;
      // rowId로 찾은 노드가 id까지 일치할 때만 반환 (다른 컴포넌트의 rowId 필드와 충돌 방지)
      if (byRowId && byRowId.id === row.id) return byRowId;

      const targetId = row.id;
      if (targetId == null) return null;
      return idIndex?.get(targetId) ?? findNodeById(originalRows, targetId);
    }

    const byIndex = originalRows[rowIndex] as Record<string, unknown> | undefined;
    if (byIndex?.id === (originalRow as Record<string, unknown>).id) return byIndex ?? null;

    const targetId = (originalRow as Record<string, unknown>).id;
    if (targetId == null) return null;
    return idIndex?.get(targetId) ?? findNodeById(originalRows, targetId);
  };

  const dataProxy = createDataProxy({
    originalRow: originalRow as Record<string, unknown>,
    treeStateFields,
    getOriginalNode,
  });

  return {
    row: rowArray,
    column: {
      index: columnIndex,
      field: columnFieldName,
      caption: typeof modernItem.column.header === 'string' ? modernItem.column.header : '',
      ...modernItem.column,
    },
    data: dataProxy,
    fieldName: columnFieldName,
  };
};

// --- Row Data Transform Utilities ---

export const isArrayRows = (rows: unknown[]): rows is unknown[][] => {
  return rows?.length > 0 && Array.isArray(rows[0]);
};

export const transformArrayRowsToObjects = <T extends Record<string, unknown>>(
  rows: unknown[][],
  columns: Array<{ field: string }>,
): T[] => {
  return rows.map((row) => {
    const obj: Record<string, unknown> = {};
    columns.forEach((col, idx) => {
      obj[col.field] = row[idx];
    });
    return obj as T;
  });
};

export const transformObjectRowToArray = <T extends Record<string, unknown>>(
  row: T,
  columns: Array<{ field: string }>,
): unknown[] => columns.map((col) => row[col.field]);

export const createFieldToIndexMap = (
  columns: Array<{ field: string }>,
): Record<string, number> => {
  return columns.reduce(
    (acc, col, idx) => {
      acc[col.field] = idx;
      return acc;
    },
    {} as Record<string, number>,
  );
};

// 트리 모드 슬롯의 getOriginalNode가 O(1) lookup 하도록 pre-order DFS로 rowId/id 맵을 빌드.
// 같은 키 중복 시 findNodeById의 pre-order DFS와 동일한 "첫 매치" 시맨틱 유지.
export const buildNodeIndices = (
  rows: unknown[] | undefined,
): {
  rowIdIndex: Map<unknown, Record<string, unknown>>;
  idIndex: Map<unknown, Record<string, unknown>>;
} => {
  const rowIdIndex = new Map<unknown, Record<string, unknown>>();
  const idIndex = new Map<unknown, Record<string, unknown>>();
  if (!rows) return { rowIdIndex, idIndex };

  const visit = (nodes: unknown[]) => {
    for (const node of nodes) {
      const n = node as Record<string, unknown>;
      const rid = n.rowId;
      const id = n.id;
      if (rid != null && !rowIdIndex.has(rid)) rowIdIndex.set(rid, n);
      if (id != null && !idIndex.has(id)) idIndex.set(id, n);
      const children = n.children;
      if (Array.isArray(children)) visit(children as unknown[]);
    }
  };
  visit(rows);
  return { rowIdIndex, idIndex };
};
